import * as coreBi from '#packages/coreBi';
import type { EditorAPI } from '#packages/editorAPI';
import * as stateManagement from '#packages/stateManagement';
import { array, media, bi } from '#packages/util';
import _ from 'lodash';
import * as categoryDefinitionValidator from './categoryDefinitionValidator';
import imagesMap from './imagesMap';
import * as sectionDefinitionValidator from './sectionDefinitionValidator';
import sectionTypesMap from './sectionTypesMap';
import {
  ImageSources,
  type PlatformAppCategoryDefinition,
  type PlatformAppItemDefinition,
  type PlatformAppWithCategoryDefinition,
  type PlatformWidgetDataForBI,
} from './types';

import type {
  DSRead,
  CompRef,
  PlatformAppDescription,
  CompStructure,
} from 'types/documentServices';
import type { Point } from '#packages/rEditor';
import {
  type Group,
  type Section,
  type Item,
  EditorPlatformHostIntegrationAPI,
} from '@wix/editor-platform-host-integration-apis';
import { type DraggableItem } from '@/addPanelInfra';
import addPanelDataConsts from '#packages/addPanelDataConsts';
import { waitForAddedCompRef } from '@/componentsAddUtils';

const platformSelectors = stateManagement.platform.selectors;
const platformActions = stateManagement.platform.actions;
const SYMBOL = 'plusBig';
const TOOLTIP = 'add_section_add_page_blankpage_tooltip';
const CATEGORY_TYPE = 'category';
const SECTION_TYPE = 'section';
const SUB_NAV_MIN_LENGTH = 5;
const DEFAULT_IMAGE_COUNT = 5;

const createImageUrlFromMap = (imageName: string) => {
  return media.getMediaUrl(imagesMap[imageName]);
};

const getDefaultImage = (categoryIndex: number): string =>
  createImageUrlFromMap(`LOGO${categoryIndex % DEFAULT_IMAGE_COUNT}`);

const setDefaultImageForItem = (subCategory: AnyFixMe, index: number) =>
  _.defaultsDeep(
    {
      props: {
        items: subCategory.props.items.map((item: AnyFixMe) =>
          _.defaults({}, item, {
            image: getDefaultImage(index),
          }),
        ),
      },
    },
    subCategory,
  );

const reportPlatformBIEvent = <
  T extends
    | typeof coreBi.events.addPanel.WIDGET_ADDED_TO_STAGE
    | typeof coreBi.events.addPanel.WIDGET_ADDED_TO_STAGE_ERROR,
>(
  editorAPI: EditorAPI,
  event: T,
  biData: {
    [K in keyof Omit<T['fields'], 'editorType' | 'brand_id'>]?: string;
  },
) => {
  editorAPI.bi.event(event, {
    ...biData,
    editorType: bi.getEditorTypeForBI(),
  });
};

const reportWidgetAdded = (
  editorAPI: EditorAPI,
  {
    presetId,
    presetName,
    appDefinitionId,
    widgetId,
    widgetName,
    structure,
  }: PlatformWidgetDataForBI,
  origin: 'add_panel_drag' | 'add_panel_click',
) => {
  reportPlatformBIEvent(
    editorAPI,
    coreBi.events.addPanel.WIDGET_ADDED_TO_STAGE,
    {
      preset_id: presetId,
      preset_name: presetName,
      origin,
      app_id: appDefinitionId,
      widget_id: widgetId,
      widget_name: widgetName,
      ...(structure
        ? {
            component_type: structure.componentType,
          }
        : {}),
    },
  );
};

const reportWidgetAddedError = (
  editorAPI: EditorAPI,
  {
    presetId,
    presetName,
    appDefinitionId,
    widgetName,
    widgetId,
    structure,
  }: PlatformWidgetDataForBI,
  origin: 'add_panel_drag' | 'add_panel_click',
  error: Error,
) => {
  const componentType = structure?.componentType;
  reportPlatformBIEvent(
    editorAPI,
    coreBi.events.addPanel.WIDGET_ADDED_TO_STAGE_ERROR,
    {
      preset_id: presetId,
      preset_name: presetName,
      origin,
      app_id: appDefinitionId,
      widget_id: widgetId,
      widget_name: widgetName,
      component_type: componentType,
      structure: JSON.stringify(structure ?? {}),
      error_data: JSON.stringify(error),
      error_message: error.toString(),
    },
  );
};

const mapDefinitionToSection = (
  editorAPI: EditorAPI,
  app: PlatformAppDescription,
  {
    title,
    desc,
    image,
    imageSource,
    id,
    presetId,
    appExtraData,
    size,
    widgetId,
  }: PlatformAppItemDefinition,
) => ({
  id: widgetId,
  presetId,
  title,
  desc,
  shouldTranslate: false,
  showSectionHeader: true,
  symbol: SYMBOL,
  tooltip: TOOLTIP,
  image:
    imageSource === ImageSources.IMAGE_LIST
      ? createImageUrlFromMap(image)
      : image,
  size,
  onItemDrop: (
    position: Point,
    _item: DraggableItem,
    containerRef: CompRef,
  ) => {
    editorAPI.panelManager.closeAllPanels();
    editorAPI.store.dispatch(
      platformActions.notifyAddWidget(app.applicationId, id, {
        ...appExtraData,
        containerRef,
        position,
        size,
      }),
    );
    reportWidgetAdded(
      editorAPI,
      {
        presetId,
        presetName: desc,
        appDefinitionId: app.appDefinitionId,
        widgetName: title,
        widgetId,
      },
      'add_panel_drag',
    );
    editorAPI.bi.event(coreBi.events.platform.add_widget_to_page, {
      source_name: 'add_panel',
      widget_id: widgetId || id,
      app_id: app.appDefinitionId,
    });
  },
  onItemClick: () => {
    editorAPI.panelManager.closeAllPanels();
    editorAPI.store.dispatch(
      platformActions.notifyAddWidget(app.applicationId, id, {
        ...appExtraData,
        size,
      }),
    );
    reportWidgetAdded(
      editorAPI,
      {
        presetId,
        presetName: desc,
        appDefinitionId: app.appDefinitionId,
        widgetName: title,
        widgetId,
      },
      'add_panel_click',
    );
    editorAPI.bi.event(coreBi.events.platform.add_widget_to_page, {
      source_name: 'add_panel',
      widget_id: widgetId || id,
      app_id: app.appDefinitionId,
    });
  },
});

const mapPlatformDefinitionToItems = (
  editorAPI: EditorAPI,
  section: Section,
  subCategoryId: string,
) => {
  const addItemToStage = async (
    item: Item,
    containerRef: CompRef,
    position?: Point,
  ) => {
    const biOrigin = position ? 'add_panel_drag' : 'add_panel_click';
    const structure = item.getStructure(containerRef, position);
    try {
      await editorAPI.transactions.run(async () => {
        await waitForAddedCompRef(
          editorAPI.components.add(containerRef, structure),
        );
        reportWidgetAdded(
          editorAPI,
          {
            presetId: item.id,
            presetName: item.title,
            appDefinitionId: subCategoryId,
            widgetId: section.id,
            widgetName: section.title,
            structure,
          },
          biOrigin,
        );
      });
    } catch (e) {
      reportWidgetAddedError(
        editorAPI,
        {
          presetId: item.id,
          presetName: item.title,
          appDefinitionId: subCategoryId,
          widgetId: section.id,
          widgetName: section.title,
          structure,
        },
        biOrigin,
        e as Error,
      );
      throw e;
    }
  };

  return section.items.map((item) => ({
    id: section.id,
    presetId: item.id,
    title: section.title,
    desc: item.title,
    shouldTranslate: false,
    showSectionHeader: true,
    symbol: SYMBOL,
    tooltip: TOOLTIP,
    image: item.image,
    size: item.size,
    ...(section.tooltipContent
      ? { tooltipContent: section.tooltipContent }
      : {}),
    onItemDrop: async (
      position: Point,
      _item: DraggableItem,
      containerRef: CompRef,
    ) => {
      editorAPI.panelManager.closeAllPanels();
      await addItemToStage(item, containerRef, position);
      editorAPI.bi.event(coreBi.events.platform.add_widget_to_page, {
        source_name: 'add_panel',
        widget_id: item.id,
        app_id: subCategoryId,
      });
    },
    onItemClick: async () => {
      editorAPI.panelManager.closeAllPanels();
      const containerRef =
        editorAPI.addPanelInfra.addPanelUtils.getContainerToAddTo(
          editorAPI,
          {
            layout: item.size,
          } as CompStructure,
          undefined,
        );
      await addItemToStage(item, containerRef);
      editorAPI.bi.event(coreBi.events.platform.add_widget_to_page, {
        source_name: 'add_panel',
        widget_id: item.id,
        app_id: subCategoryId,
      });
    },
  }));
};

const mapDefinitionToItems = (
  editorAPI: EditorAPI,
  app: PlatformAppDescription,
  { items }: PlatformAppCategoryDefinition,
) => {
  return _(items)
    .partition(sectionDefinitionValidator.validate)
    .thru(([validDefinitions, invalidDefinitions]) => {
      reportInvalidDefinitions(invalidDefinitions, SECTION_TYPE);
      return validDefinitions;
    })
    .map(_.partial(mapDefinitionToSection, editorAPI, app))
    .value();
};

const mapPlatformDefinitionToSubCategory = (
  editorAPI: EditorAPI,
  group: Group,
) => ({
  hide: false,
  id: group.id,
  type: addPanelDataConsts.SECTIONS_TYPES.GENERIC_LIST_SECTION,
  title: group.title,
  subNavigationTitle: group.title,
  presetTitle: group.title,
  subNavigationHide: !showSubNavigation(group.sections),
  showSectionHeader: true,
  shouldTranslate: false,
  disableInfoIcon: true,
  props: {
    items: group.sections.reduce(
      (items, section) =>
        items.concat(
          mapPlatformDefinitionToItems(editorAPI, section, group.id),
        ),
      [],
    ),
  },
});

const mapDefinitionToSubCategory = (
  editorAPI: EditorAPI,
  subNavigationHide: boolean,
  { app, categoryDefinition }: PlatformAppWithCategoryDefinition,
) => ({
  hide: false,
  id: app.appDefinitionId,
  type: sectionTypesMap[categoryDefinition.type],
  title: categoryDefinition.title,
  subNavigationTitle: categoryDefinition.title,
  presetTitle: categoryDefinition.header,
  subNavigationHide,
  showSectionHeader: true,
  shouldTranslate: false,
  disableInfoIcon: true,
  props: {
    items: mapDefinitionToItems(editorAPI, app, categoryDefinition),
  },
});

const showSubNavigation = (sections: AnyFixMe) =>
  sections.length >= SUB_NAV_MIN_LENGTH;

const addCategoryDefinition = (
  app: PlatformAppDescription,
  dsRead: DSRead,
): PlatformAppWithCategoryDefinition => ({
  app,
  categoryDefinition:
    platformSelectors.getAddPanelCategoryDefinitionFromManifest(
      dsRead,
      app.appDefinitionId,
    ),
});

const addCategoryDefinitionsToApps = (
  apps: PlatformAppDescription[],
  dsRead: DSRead,
): PlatformAppWithCategoryDefinition[] =>
  apps.map((app) => addCategoryDefinition(app, dsRead));

function partitionAppsByValidity(apps: PlatformAppWithCategoryDefinition[]) {
  return array.partition<PlatformAppWithCategoryDefinition>(apps, (app) =>
    categoryDefinitionValidator.validate(app.categoryDefinition),
  );
}

const createSectionsFromValidApps = (
  validDefinitions: PlatformAppWithCategoryDefinition[],
  editorAPI: EditorAPI,
) => {
  const subNavigationHide = !showSubNavigation(validDefinitions);

  const subCategories = validDefinitions.map((item) =>
    mapDefinitionToSubCategory(editorAPI, subNavigationHide, item),
  );

  return array.sortByProp(subCategories, 'title').map(setDefaultImageForItem);
};

// "Add" panel V1 is deprecated and isn't used in Classic Editor anymore
function createMyWidgetsSectionsAddPanelV1(editorAPI: EditorAPI) {
  const apps = editorAPI.dsRead.platform.getInstalledEditorApps();

  const appsWithCategoryDefinition = addCategoryDefinitionsToApps(
    apps,
    editorAPI.dsRead,
  );

  const [validDefinitions, invalidDefinitions] = partitionAppsByValidity(
    appsWithCategoryDefinition,
  );

  reportInvalidDefinitions(invalidDefinitions, CATEGORY_TYPE);

  const sections = createSectionsFromValidApps(validDefinitions, editorAPI);

  return [
    {
      sections,
    },
  ];
}

const reportInvalidDefinitions = (
  definitions: AnyFixMe,
  definitionType: AnyFixMe,
) =>
  definitions.forEach((definition: AnyFixMe) =>
    console.error(
      `Invalid ${definitionType} definition: ${JSON.stringify(definition)}`,
    ),
  );

// "getMyWidgetsSections" currently used in Classic Editor
const getMyWidgetsSections = (editorAPI: EditorAPI) => {
  const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
  const myWidgetsSections = platformAPI.panels.addPanel.getGroups();
  return myWidgetsSections.map((group: Group) =>
    mapPlatformDefinitionToSubCategory(editorAPI, group),
  );
};

export { createMyWidgetsSectionsAddPanelV1, getMyWidgetsSections };
